import { useState, useEffect, useRef } from 'react';
import { ArrowRight, CircleAlert, CircleCheck, LoaderCircle, MailOpen } from 'lucide-react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider.jsx';
import AuthShell from './AuthShell.jsx';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, isAuthenticated, isLoading } = useAuth();

  const token = searchParams.get('token');
  // 'idle' | 'loading' | 'success' | 'error'
  const [status, setStatus] = useState(token ? 'idle' : 'error');
  const [message, setMessage] = useState(
    token ? '' : 'Link inválido: token ausente.'
  );

  // Se JÁ estava logado ao chegar (logo, já verificado), não faz sentido
  // ficar aqui — vai pro painel. Snapshot no mount para não confundir com o
  // login automático que a própria confirmação dispara.
  const arrivedAuthed = useRef(null);
  useEffect(() => {
    if (isLoading) return;                    // espera resolver a sessão
    if (arrivedAuthed.current === null) {
      arrivedAuthed.current = isAuthenticated;
      if (isAuthenticated) navigate('/dashboard', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Confirmação é acionada por CLIQUE (não no load): evita que scanners de
  // email / prefetch do provedor consumam o token de uso único antes do usuário.
  const handleConfirm = async () => {
    if (!token) return;
    setStatus('loading');
    try {
      const data = await verifyEmail(token);
      setStatus('success');
      setMessage(data?.message || 'Email confirmado com sucesso!');
      // Login automático já ocorreu se vieram tokens → vai pro painel
      const next = data?.access_token ? '/dashboard' : '/login';
      setTimeout(() => navigate(next), 1600);
    } catch (err) {
      setStatus('error');
      setMessage(
        err.response?.data?.error ||
        'Link inválido ou expirado. Solicite um novo email de confirmação no login.'
      );
    }
  };

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Link inválido: token ausente.');
    }
  }, [token]);

  return (
    <AuthShell
      active="login"
      title={
        <>
          Confirme e <span className="sp-gradient-text">entre.</span>
        </>
      }
    >
      <div className="sp-verify-sent">
        {status === 'idle' && (
          <>
            <div className="sp-verify-icon">
              <MailOpen size={24} aria-hidden="true" />
            </div>
            <h2 className="sp-card-title">Confirmar seu email</h2>
            <p className="sp-card-sub">
              Clique no botão abaixo para confirmar seu email e ativar sua conta.
              Você entrará automaticamente na plataforma.
            </p>

            <button
              type="button"
              className="sp-btn-primary"
              onClick={handleConfirm}
              style={{ marginTop: '1.4rem' }}
            >
              Confirmar meu email
              <ArrowRight className="sp-btn-arrow" size={18} aria-hidden="true" />
            </button>
          </>
        )}

        {status === 'loading' && (
          <>
            <div className="sp-verify-icon">
              <LoaderCircle className="sp-spin" size={18} aria-hidden="true" />
            </div>
            <h2 className="sp-card-title">Confirmando...</h2>
            <p className="sp-card-sub">Só um instante.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="sp-verify-icon sp-verify-ok">
              <CircleCheck size={24} aria-hidden="true" />
            </div>
            <h2 className="sp-card-title">Email confirmado!</h2>
            <p className="sp-card-sub">{message} Redirecionando...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="sp-verify-icon sp-verify-err">
              <CircleAlert size={24} aria-hidden="true" />
            </div>
            <h2 className="sp-card-title">Não foi possível confirmar</h2>
            <p className="sp-card-sub">{message}</p>
          </>
        )}
      </div>

      <div className="sp-card-footer">
        <Link to="/login" className="sp-link">
          {status === 'error' ? 'Voltar ao login' : 'Ir para o login'}
        </Link>
      </div>
    </AuthShell>
  );
}
